/// <amd-module name="Feed/Legacy/Protocols/Maven/MavenOverviewContentPanel" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import { List } from "OfficeFabric/List";

import * as Utils_String from "VSS/Core/Util/String";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";

import { MavenPomMetadata } from "Feed/Common/Protocols/Maven/Contracts/Maven";
import * as PackageResources from "Feed/Common/Resources";
import { ConnectToFeedButton } from "Feed/Legacy/Components/ConnectToFeedButton";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { PackageTile } from "Feed/Legacy/Protocols/Components/PackageTile";
import * as MavenFileHelper from "Feed/Legacy/Protocols/Maven/MavenFileHelper";
import * as MavenInstallTextHelper from "Feed/Legacy/Protocols/Maven/MavenInstallTextHelper";
import { IMavenRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Maven/MavenRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export interface IMavenPackageDetailsProps extends IPackageDetailsProps {
    minimumSnapshotInstanceCount: number;
}

export interface IMavenPackageDetailsState {
    selectedKey: string;
}

const mavenClientName: string = "Maven";
const gradleClientName: string = "Gradle";

export class MavenOverviewContentPanel extends React.Component<IMavenPackageDetailsProps, IMavenPackageDetailsState> {
    constructor(props: IMavenPackageDetailsProps) {
        super(props);
        this.state = { selectedKey: mavenClientName };
    }

    public render(): JSX.Element {
        const protocolMap: { [key: string]: IPackageProtocol } = {};
        protocolMap[this.props.protocol.name] = this.props.protocol;

        const mavenMetadata: MavenPomMetadata =
            this.props.packageVersion.protocolMetadata && this.props.packageVersion.protocolMetadata.data
                ? (this.props.packageVersion.protocolMetadata.data as MavenPomMetadata)
                : null;
        const dependencyClientPairs = [
            {
                key: mavenClientName,
                value: [
                    MavenInstallTextHelper.getMavenPackageInstallText(
                        this.props.packageVersion.version,
                        this.props.packageSummary.name
                    )
                ]
            },
            { key: gradleClientName, value: [this._getGradlePackageInstallText()] }
        ];
        let parentDependencyClientPairs;
        if (mavenMetadata && mavenMetadata.parent) {
            parentDependencyClientPairs = [
                {
                    key: mavenClientName,
                    value: [
                        MavenInstallTextHelper.renderMavenPackageInstallText(
                            mavenMetadata.parent.groupId,
                            mavenMetadata.parent.artifactId,
                            mavenMetadata.parent.version
                        )
                    ]
                },
                {
                    key: gradleClientName,
                    value: [
                        this._renderGradlePackageInstallText(
                            mavenMetadata.parent.groupId,
                            mavenMetadata.parent.artifactId,
                            mavenMetadata.parent.version
                        )
                    ]
                }
            ];
        }

        const isSnapShotVersion: boolean = MavenFileHelper.isSnapshotVersion(this.props.packageVersion);
        return (
            <div className="maven-overview-content-panel package-overview-content-panel">
                {!this.props.packageVersion.isDeleted && (
                    <PackageTile
                        title={PackageResources.Maven_InstallInstructions_Title}
                        cssClass="connect-to-feed-tile"
                    >
                        <ConnectToFeedButton
                            pageContext={this.props.pageContext}
                            feed={this.props.feed}
                            feedViews={this.props.feedViews}
                            protocolMap={protocolMap}
                            hubAction={HubAction.Package}
                            defaultTabId={this.state.selectedKey}
                        />
                        <span className="install-instructions-then">{PackageResources.InstallInstructions_then}</span>

                        <div className="connect-to-feed-copy-text">
                            {this.renderPivotedTextBoxWithCopy(
                                {
                                    pairs: dependencyClientPairs,
                                    tooltipBeforeCopied: PackageResources.Maven_CopyToClipBoard_BeforeCopy,
                                    tooltipAfterCopied: PackageResources.Maven_CopyToClipBoard_AfterCopy,
                                    selectedKey: this.state.selectedKey,
                                    multiLine: true,
                                    onToggle: this.toggleButtonClicked,
                                    copyAsText: true
                                },
                                "pivot1"
                            )}
                        </div>
                    </PackageTile>
                )}
                {mavenMetadata && mavenMetadata.parent && (
                    <PackageTile title={PackageResources.Maven_Metadata_Parent} cssClass="parent-artifact-tile">
                        {this.renderPivotedTextBoxWithCopy(
                            {
                                pairs: parentDependencyClientPairs,
                                tooltipBeforeCopied: PackageResources.Maven_CopyToClipBoard_BeforeCopy,
                                tooltipAfterCopied: PackageResources.Maven_CopyToClipBoard_AfterCopy,
                                selectedKey: this.state.selectedKey,
                                multiLine: true,
                                onToggle: this.toggleButtonClicked,
                                copyAsText: true
                            },
                            "pivot2"
                        )}
                    </PackageTile>
                )}
                {this.props.packageVersion.description && this.props.packageVersion.description.length > 0 ? (
                    <PackageTile title={PackageResources.Maven_Description_Title}>
                        <div className="package-description">{this.props.packageVersion.description}</div>
                    </PackageTile>
                ) : null}
                {!this.props.packageVersion.isDeleted && (
                    <PackageTile title={PackageResources.Maven_Files_Title}>
                        <List
                            items={MavenFileHelper.getFiles(this.props.packageVersion)}
                            onRenderCell={this._renderFiles}
                        />

                        <div>
                            {isSnapShotVersion
                                ? Utils_String.format(
                                      PackageResources.Maven_SnapshotRetentionPolicy_Label,
                                      this.props.minimumSnapshotInstanceCount
                                  )
                                : ""}
                        </div>
                    </PackageTile>
                )}
            </div>
        );
    }

    public toggleButtonClicked = (newSelectedText: string): void => {
        this.setState({ selectedKey: newSelectedText });
    };

    private get LegacyPlatformService() {
        return this.props.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
    }

    private renderPivotedTextBoxWithCopy(props, id: string): JSX.Element {
        const container: JSX.Element = <div key={id} id={id} />;

        this.LegacyPlatformService.requireModules(["VSSPreview/Flux/Components/PivotedTextBoxWithCopy"]).then(
            ([pivotedTextBoxWithCopyModule]) => {
                ReactDOM.render(
                    <pivotedTextBoxWithCopyModule.PivotedTextBoxWithCopy {...props} />,
                    document.getElementById(id)
                );
            }
        );

        return container;
    }

    private _getGradlePackageInstallText(): string {
        if (this.props.packageSummary.name) {
            const packageNameParts = this.props.packageSummary.name ? this.props.packageSummary.name.split(":") : "";

            if (packageNameParts.length === 2) {
                return this._renderGradlePackageInstallText(
                    packageNameParts[0],
                    packageNameParts[1],
                    this.props.packageVersion.version
                );
            }

            return PackageResources.MavenOverview_InvalidPackageError;
        }
    }

    private _renderGradlePackageInstallText(groupId: string, artifactId: string, version: string): string {
        return Utils_String.format("compile(group: '{0}', name: '{1}', version: '{2}')", groupId, artifactId, version);
    }

    private _renderFiles = (file: MavenFileHelper.IMavenFile) => {
        return (
            <a
                href="#"
                className="package-link-text"
                rel="noopener noreferrer"
                onClick={e => {
                    const url = this.props.pageContext
                        .getService<IMavenRestApiUrlGeneratorService>("IMavenRestApiUrlGeneratorService")
                        .getFileDownloadUrl(
                            this.props.feed.id,
                            this.props.packageSummary.normalizedName,
                            this.props.packageVersion.normalizedVersion,
                            file.key,
                            this.props.feed && this.props.feed.project != null
                        );
                    e.preventDefault();
                    const newWindow = window.open(url, "_blank");
                    if (newWindow) {
                        newWindow.opener = undefined;
                    }
                }}
            >
                {file.name}
            </a>
        );
    };
}
